import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bluetooth, Trash2, X } from 'lucide-react'
import { toast } from 'sonner'
import { PageContainer } from '@/components/common/PageContainer'
import { AppButton } from '@/components/rfid/AppButton'
import { EngineListSearch, engineSearchMatch } from '@/components/rfid/EngineListSearch'
import { ManualTagEntry } from '@/components/rfid/ManualTagEntry'
import { RfidAppHeader } from '@/components/rfid/RfidAppHeader'
import { ScanCaptureHint } from '@/components/scanner/ScanCaptureHint'
import { BRAND, SCAN_HERO_SOFT } from '@/constants/brand'
import { useBirthScanSubscription } from '@/hooks/useBirthScanSubscription'
import { useLocale } from '@/i18n/LocaleProvider'
import {
  ensureMockConnected,
  isMockScannerEnabled,
} from '@/services/scannerService'
import {
  engineService,
  type BirthRangeResult,
  type BirthResult,
  type EngineItemError,
} from '@/services/engine.service'
import {
  engineItemErrorLabel,
  singleEngineErrorMessage,
  summarizeEngineBatch,
  type EngineApiError,
  type EngineErrorLabels,
} from '@/utils/engineErrors'
import {
  birthRangeRequestBody,
  COUNTRY_OPTIONS,
  countryLabel,
  currentMonthYear,
  formatCount,
  previewBirthRange,
} from '@/utils/birthRange'
import { useBirthScanStore } from '@/stores/birthScanStore'
import { isScannerSessionActive, useRfidSessionStore } from '@/stores/rfidSessionStore'

type TagRow = { tagId: string; hits: number }
type BirthMode = 'scan' | 'series'

const DEFAULT_COUNTRY = '01'

export default function EngineBirthPage() {
  const navigate = useNavigate()
  const { t } = useLocale()

  const scannerConnected = useRfidSessionStore((s) => s.scannerConnected)
  const scannerPending = useRfidSessionStore((s) => s.scannerPending)
  const deviceConnected = useRfidSessionStore((s) => s.deviceConnected)
  const scannerType = useRfidSessionStore((s) => s.scannerType)

  const rawScans = useBirthScanStore((s) => s.rawScans)
  const uniqueTags = useBirthScanStore((s) => s.uniqueTags)
  const tagHitCount = useBirthScanStore((s) => s.tagHitCount)
  const clear = useBirthScanStore((s) => s.clear)
  const removeTag = useBirthScanStore((s) => s.removeTag)

  const [mode, setMode] = useState<BirthMode>('scan')
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [results, setResults] = useState<BirthResult[]>([])
  const [errors, setErrors] = useState<EngineItemError[]>([])
  const [rangeResult, setRangeResult] = useState<BirthRangeResult | null>(null)

  const [seriesStart, setSeriesStart] = useState('')
  const [seriesEnd, setSeriesEnd] = useState('')
  const [seriesCountry, setSeriesCountry] = useState(DEFAULT_COUNTRY)
  const [seriesMonthYear, setSeriesMonthYear] = useState(currentMonthYear)

  const mock = isMockScannerEnabled()

  const errorLabels = useMemo<EngineErrorLabels>(
    () => ({
      alreadyTagged: t.Engine?.alreadyTagged,
      alreadyTaggedBirth: t.Engine?.alreadyTaggedBirth,
      alreadyTaggedTag: t.Engine?.alreadyTaggedTag,
      invalidEncoding: t.Engine?.invalidEncoding,
      notBirthed: t.Engine?.notBirthed,
      invalidSku: t.Engine?.invalidSku,
      birthPartial: t.Engine?.birthPartial,
      tagPartial: t.Engine?.tagPartial,
      allFailed: t.Engine?.allFailed,
      allOk: t.Engine?.allOk,
    }),
    [t]
  )

  useEffect(() => {
    if (mock) ensureMockConnected()
  }, [mock])

  const sessionActive = isScannerSessionActive({ scannerConnected, scannerPending })
  const enabled = sessionActive || mock
  useBirthScanSubscription(mode === 'scan' && enabled)

  const rows = useMemo<TagRow[]>(() => {
    const tags = uniqueTags.filter((tagId) => engineSearchMatch(tagId, search))
    return tags.map((tagId) => ({ tagId, hits: tagHitCount[tagId] ?? 1 }))
  }, [uniqueTags, tagHitCount, search])

  const seriesPreview = useMemo(
    () =>
      previewBirthRange({
        start: seriesStart,
        end: seriesEnd,
        country_code: seriesCountry,
        month_year: seriesMonthYear,
      }),
    [seriesStart, seriesEnd, seriesCountry, seriesMonthYear]
  )

  const scannerLabel = deviceConnected?.rfid_device ?? scannerType ?? 'scanner'
  const searchActive = search.trim().length > 0
  const matchLabel = searchActive
    ? (t.Engine?.searchShowing ?? 'Showing {{shown}} of {{total}}')
        .replace('{{shown}}', String(rows.length))
        .replace('{{total}}', String(uniqueTags.length))
    : undefined

  const onClear = () => {
    clear()
    setSearch('')
    setResults([])
    setErrors([])
    setRangeResult(null)
    toast.message(t.Engine?.birthPoolCleared ?? 'Birth scan pool cleared')
  }

  const onClearSeries = () => {
    setSeriesStart('')
    setSeriesEnd('')
    setSeriesCountry(DEFAULT_COUNTRY)
    setSeriesMonthYear(currentMonthYear())
    setRangeResult(null)
    setErrors([])
  }

  const onBirth = async () => {
    if (uniqueTags.length === 0) {
      toast.error(t.Engine?.needScan ?? 'Scan at least one RFID tag')
      return
    }

    setLoading(true)
    setResults([])
    setErrors([])
    setRangeResult(null)
    try {
      const codes = [...uniqueTags]
      if (codes.length === 1) {
        const row = await engineService.birthOne(codes[0])
        setResults([row])
        clear()
        setSearch('')
        toast.success(
          row.already_existed
            ? t.Engine?.birthExists ?? 'Already birthed'
            : t.Engine?.birthOk ?? 'Tag birthed'
        )
      } else {
        const res = await engineService.birthMany(codes)
        setResults(res.results)
        setErrors(res.errors)
        const failed = new Set(res.errors.map((e) => e.rfid_code.toUpperCase()))
        if (failed.size === 0) {
          clear()
          setSearch('')
        } else {
          for (const code of codes) {
            if (!failed.has(code.toUpperCase())) removeTag(code)
          }
        }
        const summary = summarizeEngineBatch('birth', res.results.length, res.errors, errorLabels)
        if (summary.tone === 'success') toast.success(summary.message)
        else if (summary.tone === 'warning') toast.warning(summary.message)
        else toast.error(summary.message)
      }
    } catch (err) {
      const e = err as EngineApiError
      if (e.code === 'ALREADY_TAGGED' && uniqueTags[0]) {
        setErrors([
          {
            rfid_code: uniqueTags[0],
            error: e.message || 'Already tagged',
            code: 'ALREADY_TAGGED',
          },
        ])
      }
      toast.error(singleEngineErrorMessage(e, errorLabels))
    } finally {
      setLoading(false)
    }
  }

  const onBirthSeries = async () => {
    if (!seriesPreview.valid) {
      toast.error(seriesPreview.error)
      return
    }
    const { preview } = seriesPreview
    const confirmMsg = (
      t.Engine?.birthSeriesConfirm ??
      'Birth {{count}} tags from {{start}} to {{end}}?'
    )
      .replace('{{count}}', formatCount(preview.expected_count))
      .replace('{{start}}', preview.start_rfid_code)
      .replace('{{end}}', preview.end_rfid_code)

    if (!window.confirm(confirmMsg)) return

    setLoading(true)
    setResults([])
    setErrors([])
    setRangeResult(null)
    try {
      const data = await engineService.birthRange(birthRangeRequestBody(preview))
      setRangeResult(data)
      toast.success(
        (t.Engine?.birthSeriesOk ?? 'Birthed {{created}} of {{expected}} tags')
          .replace('{{created}}', formatCount(data.created))
          .replace('{{expected}}', formatCount(data.expected_count))
      )
    } catch (err) {
      const e = err as EngineApiError
      toast.error(singleEngineErrorMessage(e, errorLabels))
    } finally {
      setLoading(false)
    }
  }

  const modeTabs = (
    <div className="flex shrink-0 gap-1 rounded-xl border border-border bg-muted/40 p-1">
      {(['scan', 'series'] as const).map((m) => {
        const active = mode === m
        const label =
          m === 'scan'
            ? t.Engine?.birthModeScan ?? 'Scan'
            : t.Engine?.birthModeSeries ?? 'Series'
        return (
          <button
            key={m}
            type="button"
            onClick={() => setMode(m)}
            className="flex-1 rounded-lg px-3 py-2 text-xs font-bold transition-colors"
            style={{
              backgroundColor: active ? BRAND : 'transparent',
              color: active ? '#fff' : undefined,
            }}
          >
            {label}
          </button>
        )
      })}
    </div>
  )

  return (
    <PageContainer fluid className="!flex !h-full !min-h-0 !flex-col !px-0 !py-0" stack={false}>
      <div className="rfid-web-frame">
        <RfidAppHeader
          title={t.Engine?.birthTitle ?? 'Birth tags'}
          subtitle={t.Engine?.birthSubtitle ?? 'Register LEAP RFID codes with no SKU'}
          onBack={() => navigate('/engine')}
          right={
            mode === 'scan' ? <ScanCaptureHint armed={enabled} showStatus={false} /> : undefined
          }
        />

        <div className="rfid-wizard-body !flex !min-h-0 !flex-1 !flex-col !gap-3 !overflow-hidden">
          {modeTabs}

          {mode === 'scan' ? (
            <>
              <div
                className="flex shrink-0 items-center rounded-2xl px-4 py-5"
                style={{ backgroundColor: SCAN_HERO_SOFT }}
              >
                <div className="flex flex-1 flex-col items-center">
                  <div className="text-4xl font-black tabular-nums leading-none text-white">
                    {rawScans}
                  </div>
                  <div className="mt-1 text-[11px] font-bold text-white/60">
                    {t.Engine?.birthRawScans ?? 'Raw scans'}
                  </div>
                </div>
                <div className="h-12 w-px bg-white/20" />
                <div className="flex flex-1 flex-col items-center">
                  <div className="text-4xl font-black tabular-nums leading-none text-white">
                    {uniqueTags.length}
                  </div>
                  <div className="mt-1 text-[11px] font-bold text-white/60">
                    {t.Engine?.birthUniqueTags ?? 'Unique tags'}
                  </div>
                </div>
              </div>

              {!enabled ? (
                <button
                  type="button"
                  onClick={() => navigate('/scanner')}
                  className="flex shrink-0 items-center justify-center gap-2.5 rounded-xl border-[1.5px] border-border bg-card px-3.5 py-3.5 text-[13px] font-bold text-foreground"
                >
                  <Bluetooth className="h-[18px] w-[18px]" style={{ color: BRAND }} strokeWidth={2} />
                  {t.Engine?.scannerNotConnected ?? 'Scanner not connected — tap to connect'}
                </button>
              ) : (
                <p className="m-0 shrink-0 text-center text-xs font-semibold leading-[17px] text-muted-foreground">
                  {(
                    t.Engine?.birthScanHint ??
                    'Press the trigger on {{scanner}} to scan tags, then Birth.'
                  ).replace('{{scanner}}', scannerLabel)}
                </p>
              )}

              {mock ? (
                <ManualTagEntry
                  compact
                  className="!mb-0 shrink-0"
                  placeholder={t.Engine?.rfidCode ?? 'RFID code'}
                />
              ) : null}

              <div className="flex shrink-0 items-center justify-between gap-2">
                <div className="text-[13px] font-black text-foreground">
                  {t.Engine?.birthUniqueTags ?? 'Unique tags'}
                  {uniqueTags.length > 0 ? (
                    <span className="ml-1.5 text-[11px] font-bold text-muted-foreground">
                      ({uniqueTags.length})
                    </span>
                  ) : null}
                </div>
                <button
                  type="button"
                  onClick={onClear}
                  disabled={uniqueTags.length === 0 && results.length === 0 && errors.length === 0}
                  className="inline-flex items-center gap-1.5 text-xs font-bold text-muted-foreground disabled:opacity-40"
                >
                  <Trash2 className="h-4 w-4" />
                  {t.Engine?.clear ?? 'Clear'}
                </button>
              </div>

              {uniqueTags.length > 0 ? (
                <EngineListSearch
                  value={search}
                  onChange={setSearch}
                  placeholder={t.Engine?.searchBirth ?? t.Engine?.searchScanned ?? 'Search RFID codes…'}
                  matchCount={rows.length}
                  totalCount={uniqueTags.length}
                  matchLabel={matchLabel}
                  aria-label={t.Engine?.searchBirth ?? 'Search birthed tags'}
                />
              ) : null}

              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain rounded-[14px] border border-border bg-card">
                {uniqueTags.length === 0 ? (
                  <p className="m-0 px-4 py-6 text-center text-xs font-semibold text-muted-foreground">
                    {t.Engine?.birthEmpty ?? 'No tags yet — press the trigger to scan'}
                  </p>
                ) : rows.length === 0 ? (
                  <p className="m-0 px-4 py-6 text-center text-xs font-semibold text-muted-foreground">
                    {t.Engine?.noSearchMatch ?? `No tags match “${search.trim()}”`}
                  </p>
                ) : (
                  <ul className="m-0 list-none p-0">
                    {rows.map((row, index) => (
                      <li
                        key={row.tagId}
                        className="flex items-center gap-2.5 px-3.5 py-3"
                        style={{
                          borderTop: index === 0 ? undefined : '1px solid var(--border)',
                        }}
                      >
                        <span className="min-w-0 flex-1 break-all font-mono text-[11.5px] font-bold text-foreground">
                          {row.tagId}
                        </span>
                        {row.hits > 1 ? (
                          <span
                            className="shrink-0 rounded-lg px-2 py-0.5 text-[10px] font-black"
                            style={{
                              color: BRAND,
                              backgroundColor: `${BRAND}18`,
                            }}
                          >
                            ×{row.hits}
                          </span>
                        ) : null}
                        <button
                          type="button"
                          onClick={() => removeTag(row.tagId)}
                          className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                          aria-label={`Remove ${row.tagId}`}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {results.length > 0 ? (
                <div className="max-h-28 shrink-0 space-y-1.5 overflow-y-auto">
                  <div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                    {t.Engine?.results ?? 'Results'}
                  </div>
                  {results.map((r) => (
                    <div
                      key={r.rfid_code}
                      className="rounded-lg border border-border bg-card px-2.5 py-1.5 text-xs"
                    >
                      <span className="font-mono font-semibold">{r.rfid_code}</span>
                      <span className="ml-2 text-muted-foreground">
                        {r.already_existed ? 'existing' : 'created'} · {r.status}
                      </span>
                    </div>
                  ))}
                </div>
              ) : null}

              {errors.length > 0 ? (
                <div className="max-h-28 shrink-0 space-y-1.5 overflow-y-auto">
                  <div className="text-[10px] font-bold uppercase tracking-wide text-red-600">
                    {t.Engine?.errors ?? 'Errors'}
                  </div>
                  {errors.map((err) => (
                    <div
                      key={`${err.rfid_code}-${err.code}`}
                      className="rounded-lg border border-red-200 bg-red-50 px-2.5 py-1.5 text-xs"
                    >
                      <span className="font-mono font-semibold">{err.rfid_code}</span>
                      <span className="ml-2 text-red-700">
                        {engineItemErrorLabel(err, errorLabels)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : null}

              <div className="shrink-0 space-y-2">
                {!enabled ? (
                  <AppButton block onClick={() => navigate('/scanner')}>
                    {t.Engine?.connectScanner ?? 'Connect scanner'}
                  </AppButton>
                ) : (
                  <AppButton block loading={loading} disabled={uniqueTags.length === 0} onClick={onBirth}>
                    {t.Engine?.birthAction ?? 'Birth'}
                    {uniqueTags.length > 0 ? ` (${uniqueTags.length})` : ''}
                  </AppButton>
                )}
              </div>
            </>
          ) : (
            <>
              <p className="m-0 shrink-0 text-center text-xs font-semibold leading-[17px] text-muted-foreground">
                {t.Engine?.birthSeriesHint ??
                  'Enter the start and end of a LEAP number series (full code or 10-digit serial). Count is inclusive.'}
              </p>

              <div className="flex shrink-0 flex-col gap-2 overflow-y-auto">
                <label className="text-[11px] font-bold text-muted-foreground">
                  {t.Engine?.birthSeriesStart ?? 'Start'}
                  <input
                    value={seriesStart}
                    onChange={(e) => setSeriesStart(e.target.value.toUpperCase())}
                    placeholder={t.Engine?.birthSeriesPlaceholder ?? 'LEAP… or 10-digit serial'}
                    className="mt-1 w-full rounded-xl border border-border bg-card px-3 py-2.5 font-mono text-[12px] font-semibold text-foreground outline-none focus:border-[color:var(--brand,#0f766e)]"
                  />
                </label>
                <label className="text-[11px] font-bold text-muted-foreground">
                  {t.Engine?.birthSeriesEnd ?? 'End'}
                  <input
                    value={seriesEnd}
                    onChange={(e) => setSeriesEnd(e.target.value.toUpperCase())}
                    placeholder={t.Engine?.birthSeriesPlaceholder ?? 'LEAP… or 10-digit serial'}
                    className="mt-1 w-full rounded-xl border border-border bg-card px-3 py-2.5 font-mono text-[12px] font-semibold text-foreground outline-none focus:border-[color:var(--brand,#0f766e)]"
                  />
                </label>

                {(!seriesPreview.valid && seriesPreview.error.includes('country')) ||
                (!seriesPreview.valid && seriesPreview.error.includes('MMYY')) ||
                (seriesPreview.valid && seriesPreview.preview.needsCountryMonth) ||
                /^\d{10}$/.test(seriesStart.trim()) ||
                /^\d{10}$/.test(seriesEnd.trim()) ? (
                  <div className="grid grid-cols-2 gap-2">
                    <label className="text-[11px] font-bold text-muted-foreground">
                      {t.Engine?.country ?? 'Country'}
                      <select
                        value={seriesCountry}
                        onChange={(e) => setSeriesCountry(e.target.value)}
                        className="mt-1 w-full rounded-xl border border-border bg-card px-3 py-2.5 text-[12px] font-semibold text-foreground"
                      >
                        {COUNTRY_OPTIONS.map((c) => (
                          <option key={c.code} value={c.code}>
                            {c.label} ({c.code})
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="text-[11px] font-bold text-muted-foreground">
                      {t.Engine?.monthYear ?? 'Month / year'}
                      <input
                        value={seriesMonthYear}
                        onChange={(e) =>
                          setSeriesMonthYear(e.target.value.replace(/\D/g, '').slice(0, 4))
                        }
                        placeholder="MMYY"
                        className="mt-1 w-full rounded-xl border border-border bg-card px-3 py-2.5 font-mono text-[12px] font-semibold text-foreground"
                      />
                    </label>
                  </div>
                ) : null}

                <div
                  className="rounded-2xl px-4 py-4 text-center"
                  style={{ backgroundColor: SCAN_HERO_SOFT }}
                >
                  {seriesPreview.valid ? (
                    <>
                      <div className="text-3xl font-black tabular-nums text-white">
                        {formatCount(seriesPreview.preview.expected_count)}
                      </div>
                      <div className="mt-1 text-[11px] font-bold text-white/70">
                        {(t.Engine?.birthSeriesPreview ??
                          '{{count}} tags · {{country}} · {{mmyy}} · {{start}}–{{end}}')
                          .replace('{{count}}', formatCount(seriesPreview.preview.expected_count))
                          .replace('{{country}}', countryLabel(seriesPreview.preview.country_code))
                          .replace('{{mmyy}}', seriesPreview.preview.month_year)
                          .replace('{{start}}', seriesPreview.preview.start_serial)
                          .replace('{{end}}', seriesPreview.preview.end_serial)}
                      </div>
                    </>
                  ) : (
                    <div className="text-xs font-semibold text-white/70">
                      {seriesStart.trim() || seriesEnd.trim()
                        ? seriesPreview.error
                        : t.Engine?.birthSeriesEmpty ?? 'Enter start and end to preview count'}
                    </div>
                  )}
                </div>

                {rangeResult ? (
                  <div className="rounded-xl border border-border bg-card px-3 py-2.5 text-xs space-y-1">
                    <div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                      {t.Engine?.results ?? 'Results'}
                    </div>
                    <div>
                      {(t.Engine?.birthSeriesSummary ??
                        'Created {{created}} · already birthed {{existed}} · already tagged {{tagged}}')
                        .replace('{{created}}', formatCount(rangeResult.created))
                        .replace('{{existed}}', formatCount(rangeResult.already_existed))
                        .replace('{{tagged}}', formatCount(rangeResult.already_tagged))}
                    </div>
                    <div className="font-mono text-[10px] text-muted-foreground break-all">
                      {rangeResult.start_rfid_code} → {rangeResult.end_rfid_code}
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="mt-auto shrink-0 space-y-2">
                <button
                  type="button"
                  onClick={onClearSeries}
                  className="inline-flex w-full items-center justify-center gap-1.5 text-xs font-bold text-muted-foreground"
                >
                  <Trash2 className="h-4 w-4" />
                  {t.Engine?.clear ?? 'Clear'}
                </button>
                <AppButton
                  block
                  loading={loading}
                  disabled={!seriesPreview.valid}
                  onClick={onBirthSeries}
                >
                  {t.Engine?.birthSeriesAction ?? 'Birth series'}
                  {seriesPreview.valid
                    ? ` (${formatCount(seriesPreview.preview.expected_count)})`
                    : ''}
                </AppButton>
              </div>
            </>
          )}
        </div>
      </div>
    </PageContainer>
  )
}
